import axios, { AxiosRequestConfig } from 'axios';
import {
  addMonths,
  eachMonthOfInterval,
  format,
  isDate,
  parseISO,
} from 'date-fns';
import FileDownload from 'js-file-download';

import client from 'api/client';
import { settings } from 'config/settings';

import { isIsoDateString } from './formatHelper';
import { getToken } from './getToken';

interface ApiData extends AxiosRequestConfig {
  path: string;
  authRequired?: boolean;
}

export const apiRequest = async <T>(apiData: ApiData) => {
  try {
    const { path, authRequired = true, ...rest } = apiData;
    const { method, headers, data, params, responseType } = rest;
    const { API_BASE_URL, API_DEBUGGING } = settings;
    const url = API_BASE_URL + path;
    const token = getToken();

    if (!token && authRequired) {
      throw new Error('No auth token provided');
    }

    const newHeaders = {
      ...headers,
      Authorization: `Bearer ${token}`,
    };
    if (API_DEBUGGING) {
      console.log(`API REQUEST - ${path}:`, {
        method,
        url,
        headers: newHeaders,
        data,
        params,
        responseType,
      });
    }

    const response = await client.request<T>({
      url,
      ...rest,
      headers: newHeaders,
      timeout: 1200100,
    });
    if (API_DEBUGGING) {
      console.log(`API RESPONSE - ${path}:`, response);
    }
    return response;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
/* eslint-disable */
export const removeEmptyFromObject = (object: Record<string, unknown>): any => {
  return Array.isArray(object)
    ? object.map((obj) => removeEmptyFromObject(obj))
    : Object.fromEntries(
        Object.entries(object)
          .filter(([_, v]) => v != null)
          .map(([k, v]) => [
            k,
            v === Object(v) ? removeEmptyFromObject(v as any) : v,
          ]),
      );
};

export interface AxiosResponse<T = any> {
  data: T;
  status: number;
  statusText: string;
  headers: any;
  config: AxiosRequestConfig;
  request?: any;
}
export const apiRequests = async <T>(requestParams: ApiData[]) => {
  try {
    const { API_BASE_URL, API_DEBUGGING } = settings;
    const requests = requestParams.map((request) => {
      const {
        path,
        authRequired = true,
        method,
        headers,
        data,
        params,
        ...rest
      } = request;

      const url = API_BASE_URL + path;
      const token = getToken();
      console.log('token', token);
      const newHeaders = {
        ...headers,
        Authorization: `Bearer ${token}`,
      };
      if (API_DEBUGGING) {
        console.log(`API REQUEST - ${path}:`, {
          method,
          url,
          headers: newHeaders,
          data,
          params,
        });
      }
      return client.request<T>({
        url,
        ...rest,
        method,
        data: data,
        headers: newHeaders,
        timeout: 1200100,
      });
    });
    const responses = await axios.all(requests);
    if (API_DEBUGGING) {
      responses.forEach((response) => {
        const { statusText, config } = response;
        console.log(`API RESPONSE - ${config.url}:`, statusText);
      });
    }
    return responses;
  } catch (e) {
    console.error(e);
    throw e;
  }
};
/* eslint-enable */
const date_fields = [
  'birthdate',
  'startDate',
  'endDate',
  'employment_start_date',
  'employment_end_date',
  'validated_work_claim_start_date',
  'validated_work_claim_end_date',
  'employer_provided_date_of_birth',
];
export const handleConvertToApi = (body: any) => {
  if (body === null || body === undefined || typeof body !== 'object') {
    return body;
  }

  for (const key of Object.keys(body)) {
    const value = body[key];
    if (date_fields.indexOf(key) > -1) {
      if (isDate(value)) {
        body[key] = format(value, 'yyyy-MM-dd');
      }
    } else if (key === 'firstname' || key === 'lastname') {
      if (value === 'Unknown') {
        body[key] = 'FNU';
      }
    } else if (typeof value === 'object') {
      handleConvertToApi(value);
    }
  }
};

export const handleConvertFromApi = (body: any) => {
  if (body === null || body === undefined || typeof body !== 'object') {
    return body;
  }

  for (const key of Object.keys(body)) {
    const value = body[key];
    if (date_fields.indexOf(key) > -1) {
      if (isIsoDateString(value)) {
        body[key] = parseISO(value);
      }
    } else if (key === 'firstname' || key === 'lastname') {
      if (value === 'FNU') {
        body[key] = 'Unknown';
      }
    } else if (typeof value === 'object') {
      handleConvertFromApi(value);
    }
  }
};

export const downloadFile = (props: { headers: any; data: ArrayBuffer }) => {
  try {
    const { data, headers } = props;
    const blob = new Blob([data]);
    const mime = headers['content-type'];

    const filename = headers['content-disposition']
      .split(/;(.+)/)[1]
      .split(/=(.+)/)[1]
      .replace(/['"]+/g, '');
    FileDownload(blob, filename, mime);
  } catch (e) {
    console.log(e);
    throw e;
  }
};
export const downloadBlobAsFile = (props: { headers: any; data: Blob }) => {
  try {
    const { data, headers } = props;
    const mime = headers['content-type'];
    console.log(headers);
    console.log(data);
    const filename = headers['content-disposition']
      .split(/;(.+)/)[1]
      .split(/=(.+)/)[1]
      .replace(/['"]+/g, '');
    FileDownload(data, filename, mime);
  } catch (e) {
    console.log(e);
    throw e;
  }
};
export const getErrorDetail = (props: {
  error: unknown;
  fallback?: string;
}): string => {
  const { error, fallback = '--' } = props;
  if (typeof error === 'object') {
    //@ts-ignore
    return error?.response?.data?.detail || fallback;
  }
  return fallback;
};

export const getMonthDateRanges = (year: number) => {
  const months = eachMonthOfInterval({
    start: new Date(year, 1, 0),
    end: new Date(year, 12, 0),
  });
  return months.map((month) => ({
    start_date: month,
    end_date: addMonths(month, 1),
  }));
};
